// src/utils/appointments/practitionerColour.ts
//
// S4 (86d420rzv) — one shared resolver for a practitioner's display colour + initials.
// Replaces the five call sites that used to import StaffLegend's generateStaffColor /
// getStaffInitials directly.

import { TeamMember } from "../../types/admin/TeamTypes";
import type { UserUuid } from "../../types/identity";

/**
 * ⚠️ NOT the live colour mechanism. This is the original 10-entry hashed palette,
 * kept ONLY as a null-safety fallback for a practitioner created before the S3 colour
 * backfill ran, or one whose auto-allocation failed. Live colours come from
 * `team_member_info.color`, allocated server-side by the auth service from the
 * (much larger, and NOT duplicated here — see S4 brief D6) selectable palette. Do not
 * delete this as dead code, and do not extend it — a collision here is expected and
 * accepted for the pre-backfill edge case; it is not the bug S4 fixes.
 */
const FALLBACK_PALETTE = [
	"#E53E3E", // Red
	"#38B2AC", // Teal
	"#F6AD55", // Orange
	"#805AD5", // Purple
	"#48BB78", // Green
	"#4299E1", // Blue
	"#ED64A6", // Pink
	"#ECC94B", // Yellow
	"#9F7AEA", // Purple-light
	"#F687B3", // Pink-light
];

function fallbackColour(id: UserUuid): string {
	const hash = [...String(id ?? "")].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 0);
	return FALLBACK_PALETTE[hash % FALLBACK_PALETTE.length];
}

/**
 * Resolve a practitioner's display colour.
 *
 * `team_member_info.color` is stored data returned by the auth service — validated and
 * allocated there (S1-S3) — not a design token, exactly like `appointment_types.color`.
 * It is deliberately NOT read from a `var(--mg-*)` CSS variable; do not "fix" this call
 * site into one.
 */
export function practitionerColour(member: Pick<TeamMember, "id" | "team_member_info">): string {
	return member.team_member_info?.color ?? fallbackColour(member.id);
}

/**
 * Initials for a practitioner's avatar. Moved verbatim from StaffLegend's
 * getStaffInitials — behaviour unchanged.
 */
export function practitionerInitials(firstName: string, lastName?: string): string {
	if (!firstName) return "?";
	const initials = firstName.charAt(0) + (lastName ? lastName.charAt(0) : "");
	return initials.toUpperCase();
}
